import React, { useEffect, useId, useState } from 'react';
import type { ChangeEvent, ComponentProps } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import {
  backgroundColor,
  backgroundImage,
  backgroundPosition,
  backgroundRepeat,
  color,
  fontWeight,
  space,
} from 'styled-system';
import type {
  BackgroundImageProps,
  BackgroundPositionProps,
  BackgroundRepeatProps,
  FontWeightProps,
} from 'styled-system';

import arrowDown from 'common/assets/icons/arrow-down.svg';
import Input from 'common/components/Input/StatefulInput';
import VisuallyHidden from 'common/components/VisuallyHidden';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { BackgroundColorProps, ColorProps, SpaceProps } from 'common/theme/system-types';
import { cursor } from 'common/theme/system-utilities';
import type { CursorProps } from 'common/theme/system-utilities';
import type { Reason } from './types';

interface DropdownProps {
  reasons: Array<Nullable<Reason>>;
  onSelect: (value: Nullable<string>) => void;
  initialReason: Nullable<string>;
  ariaLabelledBy: string;
}

const SELECT_COLOR_DEFAULT = 'sys.neutral.border.hover';
const SELECT_COLOR_WITH_VALUE = 'sys.neutral.border.strong';

const UnstyledSelect = styled(Input)<
  ComponentProps<typeof Input> &
    SpaceProps &
    BackgroundColorProps &
    BackgroundImageProps &
    BackgroundRepeatProps &
    BackgroundPositionProps &
    CursorProps &
    ColorProps &
    FontWeightProps
>`
  ${space}
  ${backgroundColor}
  ${backgroundImage}
  ${backgroundRepeat}
  ${backgroundPosition}
  ${cursor}
  ${color}
  ${fontWeight}

  appearance: none;
`;

const Select = (props: ComponentProps<typeof UnstyledSelect>) => (
  <UnstyledSelect
    as="select"
    mb={['sp_16', 'sp_20']}
    cursor="pointer"
    backgroundColor="sys.neutral.background.default"
    backgroundImage={`url('${arrowDown}')`}
    backgroundRepeat="no-repeat, repeat"
    backgroundPosition="right 8px top 50%, 0 0"
    {...props}
  />
);

const findReasonRecursive = (
  reason: string,
  reasons: Array<Nullable<Reason>>
): Nullable<Reason> => {
  const foundReason = reasons.find(r => r?.id === reason);
  if (foundReason || !reason.includes('.')) {
    return foundReason ?? null;
  }
  const upperReason = reason.substring(0, reason.lastIndexOf('.'));

  return findReasonRecursive(upperReason, reasons);
};

const useRecursiveDropdown = ({
  reasons,
  onSelect,
  initialReason,
}: Omit<DropdownProps, 'ariaLabelledBy'>) => {
  const [currentValue, setCurrentValue] = useState<Nullable<Reason>>(null);
  const onSelectChange = ({ currentTarget: { value } }: ChangeEvent<HTMLInputElement>) => {
    const currentReason = reasons.find(reason => reason?.id === value);

    setCurrentValue(currentReason ?? null);

    if (currentReason?.values?.length === 0) {
      onSelect(currentReason.id);
    } else {
      onSelect(null);
    }
  };

  useEffect(() => {
    setCurrentValue(null);
  }, [reasons]);

  useEffect(() => {
    if (initialReason) {
      const reason = findReasonRecursive(initialReason, reasons);
      if (reason) {
        setCurrentValue(reason);
      }
    }
  }, []);

  return { currentValue, onSelectChange };
};

export const Dropdown = ({ reasons, onSelect, initialReason, ariaLabelledBy }: DropdownProps) => {
  const { t, r } = useLocalization();
  const labelId = useId();

  const { currentValue, onSelectChange } = useRecursiveDropdown({
    reasons,
    onSelect,
    initialReason,
  });
  const selectValue = currentValue?.id || '';

  return (
    <>
      <Select
        required
        value={selectValue}
        onChange={onSelectChange}
        aria-labelledby={ariaLabelledBy}
        fontWeight={selectValue ? 'bold' : 'normal'}
        color={selectValue ? SELECT_COLOR_WITH_VALUE : SELECT_COLOR_DEFAULT}
      >
        <option value="">{t('h24_dropdown_placeholder')}</option>
        {reasons
          .filter(reason => reason !== null)
          .map(({ id, title }) => (
            <option key={id} value={id}>
              {title}
            </option>
          ))}
      </Select>

      {currentValue?.values?.length ? (
        <>
          <VisuallyHidden id={labelId}>
            {r.t('h24_select_reason', { reason: currentValue.title })}
          </VisuallyHidden>
          <Dropdown
            onSelect={onSelect}
            ariaLabelledBy={labelId}
            reasons={currentValue.values}
            initialReason={initialReason}
          />
        </>
      ) : null}
    </>
  );
};
